import { useState } from 'react';
import { Copy, Trash2, ExternalLink, Pencil, Check, X, ChevronDown, ChevronUp } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { IconButton } from '../../components/IconButton';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { useToast } from '../../app/ToastProvider';
import { useApiQueries } from '../../app/ApiClientContext';
import { copyToClipboard } from '../../lib/clipboard';
import { Input } from '../../components/Input';
import { relative } from '../../lib/relativeTime';
import { LinkActivityPanel } from './LinkActivityPanel';

export type LinkItem = {
  id: number;
  url_from: string;
  url_to: string;
  owner: { id: number; username: string; email: string | null; full_name: string | null };
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
  last_redirect_at: string | null;
};

export function LinkRow({
  item,
  showOwner,
}: {
  item: LinkItem;
  showOwner: boolean;
}) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const $api = useApiQueries();

  const isDeleted = item.deleted_at !== null;

  const [confirming, setConfirming] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draftUrlTo, setDraftUrlTo] = useState(item.url_to);
  const [expanded, setExpanded] = useState(false);

  const deleteMut = $api.useMutation('delete', '/api/v2/link');
  const updateMut = $api.useMutation('put', '/api/v2/link');

  async function doDelete() {
    try {
      await deleteMut.mutateAsync({ body: { url_from: item.url_from } } as Parameters<
        typeof deleteMut.mutateAsync
      >[0]);
      toast.push('success', 'Ссылка удалена');
      await queryClient.invalidateQueries({ queryKey: ['get', '/api/v2/link'] });
    } catch {
      toast.push('error', 'Не удалось удалить');
    } finally {
      setConfirming(false);
    }
  }

  async function saveEdit() {
    try {
      await updateMut.mutateAsync({
        body: { url_from: item.url_from, url_to: draftUrlTo, force: true },
      } as Parameters<typeof updateMut.mutateAsync>[0]);
      toast.push('success', 'Ссылка обновлена');
      setEditing(false);
      await queryClient.invalidateQueries({ queryKey: ['get', '/api/v2/link'] });
    } catch {
      toast.push('error', 'Не удалось сохранить');
    }
  }

  const liClass = [
    'border-b border-[var(--color-border)] py-3 flex flex-col gap-1',
    isDeleted ? 'opacity-60 line-through' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <li className={liClass}>
      <div className="flex items-center gap-2">
        <IconButton
          label={expanded ? 'Скрыть историю' : 'Показать историю'}
          aria-expanded={expanded}
          onClick={() => setExpanded((v) => !v)}
        >
          {expanded ? <ChevronUp size={16} strokeWidth={1.5} /> : <ChevronDown size={16} strokeWidth={1.5} />}
        </IconButton>
        <a
          href={item.url_from}
          target="_blank"
          rel="noopener noreferrer"
          className="font-mono text-sm truncate hover:underline"
        >
          {item.url_from}
        </a>
        <span className="flex-1" />
        <IconButton
          label="Копировать"
          onClick={async () => {
            try {
              await copyToClipboard(item.url_from);
              toast.push('info', 'Скопировано');
            } catch {
              toast.push('error', 'Не удалось скопировать');
            }
          }}
        >
          <Copy size={16} strokeWidth={1.5} />
        </IconButton>
        <IconButton label="Открыть цель" onClick={() => window.open(item.url_to, '_blank')}>
          <ExternalLink size={16} strokeWidth={1.5} />
        </IconButton>
        {!isDeleted && (
          <>
            <IconButton
              label="Редактировать"
              onClick={() => {
                setDraftUrlTo(item.url_to);
                setEditing((v) => !v);
              }}
            >
              <Pencil size={16} strokeWidth={1.5} />
            </IconButton>
            <IconButton label="Удалить" onClick={() => setConfirming(true)}>
              <Trash2 size={16} strokeWidth={1.5} />
            </IconButton>
          </>
        )}
      </div>

      {!editing && (
        <div className="text-xs text-[var(--color-text-muted)] truncate">
          → <a href={item.url_to} className="hover:underline">{item.url_to}</a>
        </div>
      )}

      {editing && !isDeleted && (
        <div className="pt-1 flex items-center gap-2">
          <Input
            type="url"
            value={draftUrlTo}
            onChange={(e) => setDraftUrlTo(e.target.value)}
            className="flex-1"
          />
          <IconButton label="Сохранить" onClick={() => void saveEdit()} disabled={updateMut.isPending}>
            <Check size={16} strokeWidth={1.5} />
          </IconButton>
          <IconButton label="Отмена" onClick={() => setEditing(false)}>
            <X size={16} strokeWidth={1.5} />
          </IconButton>
        </div>
      )}

      <div className="text-xs text-[var(--color-text-muted)]">
        {showOwner ? `${item.owner.full_name || item.owner.username} · ` : ''}
        {isDeleted
          ? `удалено ${relative(item.deleted_at!)}`
          : `создано ${relative(item.created_at)}`}
        {item.last_redirect_at
          ? ` · последний переход ${relative(item.last_redirect_at)}`
          : ' · переходов нет'}
      </div>

      {expanded && <LinkActivityPanel linkId={item.id} />}

      {confirming && !isDeleted && (
        <ConfirmDialog
          open={confirming}
          title="Удалить ссылку?"
          body={
            <span>
              Будет удалена короткая ссылка <code className="font-mono">{item.url_from}</code>.
            </span>
          }
          confirmLabel={deleteMut.isPending ? 'Удаляем…' : 'Удалить'}
          confirmVariant="danger"
          onConfirm={() => void doDelete()}
          onCancel={() => setConfirming(false)}
        />
      )}
    </li>
  );
}
